export const LOCAL_PATH_PLACEHOLDER = "<local-path>";

const POSIX_ROOT = String.raw`/(?:Users|home|Volumes|private|tmp|var/folders|mnt|media)/`;
// A drive letter not preceded by a letter, so `https://` never matches.
const DRIVE_ROOT = String.raw`(?<![A-Za-z0-9])[A-Za-z]:[\\/]`;
// Any root but a drive one only starts a new path after whitespace, so the
// doubled backslashes of a JSON-escaped path are not read as a UNC root.
const ROOT_AFTER_SPACE =
  String.raw`\s(?:[A-Za-z]:[\\/]|\\\\|file:|[A-Za-z]%3A(?:%5C|%2F)|${POSIX_ROOT})`;

/** Path text up to a character in `stops`, a line break, or the start of a path matching `nextPath`. */
function pathBody(stops: string, nextPath: string): string {
  return String.raw`(?:(?!${nextPath})[^${stops}\r\n])*`;
}

// Characters Windows forbids in a file or folder name end a path.
const PATH_BODY = pathBody(String.raw`:*?"<>|`, `${DRIVE_ROOT}|${ROOT_AFTER_SPACE}`);

const LOCAL_PATH_PATTERNS: readonly RegExp[] = [
  // A file URL keeps its drive colon (`file:///C:/...`).
  new RegExp(String.raw`\bfile:(?=[^\s"'<>])${pathBody(String.raw`*?"<>|`, ROOT_AFTER_SPACE)}`, "gi"),
  // An encoded path sits in a query string, so `&` ends it.
  new RegExp(
    String.raw`(?<![A-Za-z0-9])[A-Za-z]%3A(?:%5C|%2F)` +
      pathBody(String.raw`:*?"<>|&`, `${DRIVE_ROOT}|${ROOT_AFTER_SPACE}`),
    "gi"
  ),
  new RegExp(DRIVE_ROOT + PATH_BODY, "g"),
  new RegExp(String.raw`(?<![\\\w])\\{2,}(?=[\w.$-])${PATH_BODY}`, "g"),
  // Only well-known local roots, and never inside a URL or API route.
  new RegExp(String.raw`(?<![\w.:/\\-])${POSIX_ROOT}${PATH_BODY}`, "g"),
];

const NAME_CHARACTER = new RegExp(String.raw`[\p{L}\p{N}]`, "u");
const TRAILING_CHARACTER = /[\s.]/;

/**
 * Replaces one matched path. A path after an opening `'` ends at the last `'`
 * that is not followed by a letter or digit, so a name such as `Clients' Files`
 * stays whole. Trailing spaces and dots are not part of a Windows name.
 */
function redactPathMatch(match: string, offset: number, text: string): string {
  let end = match.length;
  if (text[offset - 1] === "'") {
    for (let index = match.length - 1; index > 0; index -= 1) {
      const next = match[index + 1] ?? text[offset + match.length] ?? "";
      if (match[index] === "'" && !NAME_CHARACTER.test(next)) {
        end = index;
        break;
      }
    }
  }
  while (end > 0 && TRAILING_CHARACTER.test(match[end - 1])) end -= 1;
  return LOCAL_PATH_PLACEHOLDER + match.slice(end);
}

/**
 * Replaces every local file path in `text` with `<local-path>`: Windows drive
 * and UNC paths (quoted, spaced or JSON-escaped), `file:` URLs, and macOS or
 * Linux user folders. URLs, API routes and bare filenames are left intact.
 *
 * Names may contain spaces, dots and apostrophes, so an unquoted path runs to
 * the next character Windows forbids in a name, line break or path, and any
 * prose after it is redacted with it rather than risk leaking part of a name.
 * Callers that know the exact path should replace it first to keep that prose.
 */
export function redactLocalPathText(text: string): string {
  return LOCAL_PATH_PATTERNS.reduce(
    (redacted, pattern) => redacted.replace(pattern, redactPathMatch),
    text
  );
}

/** Wraps a log sink so every line it receives has local paths redacted. */
export function withLocalPathRedaction(
  log: ((message: string) => void) | undefined
): ((message: string) => void) | undefined {
  return log ? (message) => log(redactLocalPathText(message)) : undefined;
}
